// Страница получателя подарка: выбрать время и адрес. Без цены, букета и имени отправителя.

import { useEffect, useState } from 'react';
import { api, type Slot } from '../lib/api.ts';
import { dayWord, slotText } from '../lib/format.ts';
import { ErrorNote, Icon, Spinner } from '../ui/kit.tsx';
import { AddressField, type Address } from './checkout.tsx';

export function Recipient({ token }: { token: string }) {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.gift>> | null>(null);
  const [slot, setSlot] = useState<number | null>(null);
  const [address, setAddress] = useState<Address | null>(null);
  const [extra, setExtra] = useState('');
  const [door, setDoor] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.gift(token).then(
      (g) => {
        setData(g);
        setDone(g.picked);
        setSlot(g.slots.find((s: Slot) => s.from === g.slotFrom && s.available)?.from ?? g.slots.find((s: Slot) => s.nearest)?.from ?? null);
      },
      (e) => setError(e.message),
    );
  }, [token]);

  if (!data) return <main className="screen">{error ? <ErrorNote error={error} /> : <Spinner />}</main>;
  const name = data.recipientName;

  return (
    <main className="screen" style={{ paddingTop: 28, gap: 20 }}>
      <svg className="blob" aria-hidden="true" viewBox="0 0 40 40" style={{ right: -60, top: -50, width: 200, height: 200, opacity: 0.6 }}>
        <g fill="#F7ACD6">
          <circle cx="20" cy="10" r="7.5" />
          <circle cx="29.5" cy="16.9" r="7.5" />
          <circle cx="25.9" cy="28.1" r="7.5" />
          <circle cx="14.1" cy="28.1" r="7.5" />
          <circle cx="10.5" cy="16.9" r="7.5" />
          <circle cx="20" cy="20" r="8" />
        </g>
      </svg>
      <img src="/logo.png" alt="Выдыхай" style={{ width: 150, marginLeft: -10, position: 'relative', mixBlendMode: 'multiply' }} />
      {done ? (
        <>
          <div className="stack" style={{ position: 'relative' }}>
            <h1 className="h1">Готово, жди цветы</h1>
            <p className="p">Курьер привезёт букет в выбранное окно. Что внутри, пока секрет. Если что-то изменится, позвоним.</p>
          </div>
          <div className="card stack">
            <span className="row">
              <Icon name="calendar" size={18} color="#6B604A" width={1.8} />
              {dayWord(data.date)}, {slotText(slot ?? data.slotFrom, (slot ?? data.slotFrom ?? 0) + 2)}
            </span>
            <span className="row">
              <Icon name="phone" size={18} color="#6B604A" width={1.8} />
              Курьер позвонит перед приездом
            </span>
          </div>
        </>
      ) : (
        <>
          <div className="stack" style={{ position: 'relative' }}>
            <h1 className="h1">{name ? `${name}, тебе` : 'Тебе'} приготовили цветы</h1>
            <p className="p">Что за букет, пока секрет. Подскажи, куда и когда его привезти.</p>
          </div>
          <div className="stack">
            <span className="label">{dayWord(data.date).replace(/^./, (c) => c.toUpperCase())}, в какое время удобно?</span>
            <div className="wrap">
              {data.slots.map((s) => (
                <button key={s.from} type="button" className="chip pink" disabled={!s.available} aria-pressed={slot === s.from} onClick={() => setSlot(s.from)} style={s.available ? undefined : { opacity: 0.4 }}>
                  {slotText(s.from, s.to)}
                  {s.full ? ' · занято' : ''}
                </button>
              ))}
            </div>
          </div>
          <div className="stack">
            <AddressField value={address} onChange={setAddress} label="Куда привезти" id="raddr" />
            <input className="input" placeholder="Квартира, подъезд, домофон, этаж" value={extra} maxLength={200} onChange={(e) => setExtra(e.target.value)} aria-label="Подсказка курьеру" />
            <label className="check">
              <input type="checkbox" checked={door} onChange={(e) => setDoor(e.target.checked)} />
              <span>Можно оставить у двери</span>
            </label>
          </div>
          <ErrorNote error={error} />
          <div className="bottom">
            <button
              type="button"
              className="btn"
              disabled={busy || slot === null || !address}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  await api.giftPick(token, {
                    slotFrom: slot,
                    address: address!.value,
                    addressExtra: extra || undefined,
                    lat: address!.lat ?? undefined,
                    lon: address!.lon ?? undefined,
                    insideMkad: address!.insideMkad,
                    handover: door ? 'door' : 'hand',
                  });
                  setDone(true);
                } catch (e: any) {
                  setError(e.message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Жду цветы
            </button>
            <p className="tiny center" style={{ margin: 0 }}>
              Адрес передадим только курьеру.
            </p>
          </div>
        </>
      )}
    </main>
  );
}
